/* eslint-disable react-hooks/rules-of-hooks */
import { cn } from "@/lib/utils";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "./create-content-portal.css";

const REPLACE_CONTAINER_CLASS = "content-portal-replace-mode-container";
const REPLACE_PORTAL_CLASS = "content-portal-replace-mode";

export type ContentPortalProps = React.PropsWithChildren<{
  display?: "append" | "replace";
}>;

type ContentProps = {
  className?: string;
};

export const createContentPortal = (contentId: string) => {
  let portalCounter = 0;
  const targetListeners = new Set<() => void>();
  const activePortals = new Map<
    number,
    { id: number; display: Required<ContentPortalProps["display"]> }
  >();

  const setContainerRef = (_node: HTMLDivElement | null) => {
    targetListeners.forEach((listener) => listener());
  };

  const container = ({ className }: ContentProps) => (
    <div className={className} id={contentId} ref={setContainerRef} />
  );

  const portal = ({ children, display = "append" }: ContentPortalProps) => {
    const portalId = useRef(++portalCounter);
    const [target, setTarget] = useState<HTMLElement | null>(null);

    useEffect(() => {
      const updateTarget = () => {
        const element = document.getElementById(contentId);
        setTarget((prev) => (prev === element ? prev : element));
      };

      // Initial check
      updateTarget();

      // Watch for the element to be added to DOM
      targetListeners.add(updateTarget);
      return () => {
        targetListeners.delete(updateTarget);
      };
    }, []);

    useEffect(() => {
      activePortals.set(portalId.current, { id: portalId.current, display });

      // Add class to container when replace mode is active
      const target = document.getElementById(contentId);
      if (target && display === "replace") {
        target.classList.add(REPLACE_CONTAINER_CLASS);
      }

      return () => {
        activePortals.delete(portalId.current);
        // Remove class when no replace mode portals are active
        const target = document.getElementById(contentId);
        if (target && display === "replace") {
          const hasOtherReplaceModes = Array.from(activePortals).some(
            ([_, { display }]) => {
              return display === "replace";
            },
          );
          if (!hasOtherReplaceModes) {
            target.classList.remove(REPLACE_CONTAINER_CLASS);
          }
        }
      };
    }, []);

    if (!target) return null;

    const element = (
      <div
        className={cn("contents", {
          [REPLACE_PORTAL_CLASS]: display === "replace",
        })}
      >
        {children}
      </div>
    );

    return createPortal(element, target);
  };

  const useOnContentCheck = () => {
    const [hasContent, setHasContent] = useState(false);

    useEffect(() => {
      const observer = new MutationObserver(() => {
        const target = document.getElementById(contentId);
        if (target) {
          setHasContent(target.children.length > 0);
        }
      });

      const target = document.getElementById(contentId);
      if (target) {
        observer.observe(target, {
          childList: true,
        });
        // Initial check
        setHasContent(target.children.length > 0);
      }

      return () => {
        observer.disconnect();
      };
    }, []);

    return hasContent;
  };

  return {
    ContentContainer: container,
    ContentPortal: portal,
    useOnContentCheck,
  };
};
